import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, RefreshCw, Building2, Cloud, Database } from 'lucide-react'
import { listAccounts } from '../api/accounts'
import { CreateAccountWizard } from '../components/CreateAccountWizard'
import { StarrocksSetupWizard } from '../components/StarrocksSetupWizard'
import type { Account } from '../types'

const STATUS_STYLES: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  INACTIVE: 'bg-gray-100 text-gray-600',
  SUSPENDED: 'bg-red-100 text-red-700',
}

export function AccountManagementPage() {
  const [showWizard, setShowWizard] = useState(false)
  const [setupWarehouseAccId, setSetupWarehouseAccId] = useState<number | null>(null)
  const [starrocksAccId, setStarrocksAccId] = useState<number | null>(null)

  const { data: accounts = [], isLoading, isError, refetch, isFetching } = useQuery<Account[]>({
    queryKey: ['accounts-management'],
    queryFn: listAccounts,
    staleTime: 30 * 1000,
  })

  return (
    <div className="p-6 space-y-5">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Account Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage customer accounts and their associated infrastructure.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowWizard(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            New Account
          </button>
        </div>
      </div>

      {/* Accounts table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500">Loading accounts...</p>
          </div>
        ) : isError ? (
          <div className="p-12 text-center">
            <p className="text-sm text-red-500">Failed to load accounts.</p>
            <button
              onClick={() => refetch()}
              className="mt-3 text-sm text-primary hover:underline"
            >
              Try again
            </button>
          </div>
        ) : accounts.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-600">No accounts yet</p>
            <p className="text-xs text-gray-400 mt-1">Click "New Account" to get started.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Acc ID
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Account Name
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Contact
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Email
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Industry
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Country
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Created
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {accounts.map((account) => (
                <AccountRow
                  key={account.accId}
                  account={account}
                  onSetupWarehouse={() => setSetupWarehouseAccId(account.accId)}
                  onSetupStarrocks={() => setStarrocksAccId(account.accId)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary footer */}
      {accounts.length > 0 && (
        <p className="text-xs text-gray-400">
          {accounts.length} account{accounts.length !== 1 ? 's' : ''} total
        </p>
      )}

      {/* Create wizard */}
      {showWizard && <CreateAccountWizard onClose={() => setShowWizard(false)} />}

      {/* Retrigger warehouse setup for existing account */}
      {setupWarehouseAccId && (
        <CreateAccountWizard
          existingAccId={setupWarehouseAccId}
          onClose={() => setSetupWarehouseAccId(null)}
        />
      )}

      {/* StarRocks setup wizard */}
      {starrocksAccId && (
        <StarrocksSetupWizard
          accId={starrocksAccId}
          accountName={accounts.find((a) => a.accId === starrocksAccId)?.accountName ?? `Account ${starrocksAccId}`}
          onClose={() => setStarrocksAccId(null)}
        />
      )}
    </div>
  )
}

function AccountRow({
  account,
  onSetupWarehouse,
  onSetupStarrocks,
}: {
  account: Account
  onSetupWarehouse: () => void
  onSetupStarrocks: () => void
}) {
  const statusStyle = STATUS_STYLES[account.status] ?? 'bg-gray-100 text-gray-600'

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 font-mono text-xs text-gray-600">{account.accId}</td>
      <td className="px-4 py-3 font-medium text-gray-900">{account.accountName}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle}`}>
          {account.status}
        </span>
      </td>
      <td className="px-4 py-3 text-gray-600">{account.contactName ?? '—'}</td>
      <td className="px-4 py-3 text-gray-600">{account.email ?? '—'}</td>
      <td className="px-4 py-3 text-gray-600">{account.industry ?? '—'}</td>
      <td className="px-4 py-3 text-gray-600">{account.country ?? '—'}</td>
      <td className="px-4 py-3 text-gray-400 text-xs">
        {account.createdAt ? new Date(account.createdAt).toLocaleDateString() : '—'}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={onSetupWarehouse}
            title="Setup / Retrigger Warehouse"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50"
          >
            <Cloud className="h-3.5 w-3.5" />
            Warehouse
          </button>
          <button
            onClick={onSetupStarrocks}
            title="StarRocks Setup / Backfill"
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-50"
          >
            <Database className="h-3.5 w-3.5" />
            StarRocks
          </button>
        </div>
      </td>
    </tr>
  )
}
